import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Container, Modal, Typography } from '@material-ui/core';
import { isWidthUp } from '@material-ui/core/withWidth';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

import PathwaySmallIcon from '../../assets/icons/PathwaySmallIcon';
import { JSBlock } from '../../components/JSBlock';
import JoinNowBlock from '../../components/JoinNowBlock';
import { Loader } from '../../components/Loader';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';
import SideVideoCard from '../../components/SideVideoCard';
import VideoPlayer from '../../components/VideoPlayer/VideoPlayer';
import { RM_TAGS } from '../../const';
import { useRmTag } from '../../context/RmContext';
import { GET_PAGE_BY_URL } from '../../graphql/pages';
import { GET_VIDEOS_EXIST_IN_USER_WATCH_LIST } from '../../graphql/pathways';
import {
  CHECK_IF_VIDEO_EXISTS_IN_WATCHLIST,
  GET_CONTINUE_WATCHING_VIDEO_LINKS,
  GET_NEXT_IN_PATHWAY_VIDEO,
  GET_RATING_FOR_ENTITY,
  GET_RELATED_VIDEOS,
  GET_VIDEO_BY_SLUG_OR_ID,
} from '../../graphql/videos';
import { useWidth } from '../../theme';
import {
  GetPageByUrlArgs,
  GetVideoBySlugOrIdArgs,
  GetVideoBySlugOrIdData,
  GetVideoNextInPathwayArgs,
  GetVideoNextInPathwayData,
  LearnerInfo,
  listReviewsArgs,
  Query,
  QueryListVideosYouMightAlsoLikeArgs,
  QueryVideosExistInUserWatchListArgs,
  ReviewableType,
  Video,
  VideoContinueWatchingLinksArgs,
  VideoContinueWatchingLinksData,
  VideoExistsInUserWatchListArgs,
} from '../../types';
import Cookies from '../../utils/cookies';
import {
  collectValueByKey,
  filterPathwaysArrByVideoId,
  generateContinueWatchingLink,
  generateRandomImageUrl,
  getLinksObj,
  getQueryStringParams,
  replaceQuotes,
} from '../../utils/helpers';
import { useAuth, useMathJaxScriptLoader } from '../../utils/hooks';
import useVerse from '../../utils/hooks/useVerse';
import { CustomPage } from '../CustomPage';
import NotFoundPage from '../NotFoundPage';
import ImageBackground from './components/ImageBackground';
import JoinNowModal from './components/JoinNowModal';
import LoggedInHeader from './components/LoggedInHeader';
import LoggedOutHeader from './components/LoggedOutHeader';
import PathwayNavigation from './components/PathwayNavigation';
import { PathwayPlayerMenu } from './components/PathwayPlayerMenu';
import Rating from './components/Rating/Rating';
import { VideoPageSeoBlock } from './components/VideoPageSeoBlock';
import VideoTabsContainer from './components/VideoTabsContainer/VideoTabsContainer';
import VideoTitle from './components/VideoTitle';
import {
  LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME,
  PAYWALLED_CONTENT_SELECTORS,
  VIDEO_PAGE_CUSTOM_BLOCKS_URL,
} from './const';
import useStyles, { useJoinNowStyles } from './styles';

const getVideoPageJsonData = (
  title = '',
  description = '',
  keyLearningObjectives: (string | undefined)[] | undefined = [],
) => {
  const _title = replaceQuotes(title);
  const _description = replaceQuotes(description);
  const _keyLearningObjectives = keyLearningObjectives.map((item) =>
    item ? replaceQuotes(item) : item,
  );
  return `{
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "${_title}",
    "description": "${_description}",
    "provider": {
        "@type": "Organization",
        "name": "Finance Unlocked",
        "sameAs": "https://financeunlocked.com"
    },
    "teaches": "${_keyLearningObjectives.join(', ')}",
    "learningResourceType": "video",
    "isAccessibleForFree": "False",
    "hasPart": [
      {
        "@type": "WebPageElement",
        "isAccessibleForFree": "True",
        "cssSelector": ".${PAYWALLED_CONTENT_SELECTORS.OVERVIEW}"
      }, {
        "@type": "WebPageElement",
        "isAccessibleForFree": "True",
        "cssSelector": ".${PAYWALLED_CONTENT_SELECTORS.SUMMARY}"
      }, {
        "@type": "WebPageElement",
        "isAccessibleForFree": "False",
        "cssSelector": ".${PAYWALLED_CONTENT_SELECTORS.VIDEO_PLAY_BTN}"
      }, {
        "@type": "WebPageElement",
        "isAccessibleForFree": "False",
        "cssSelector": ".${PAYWALLED_CONTENT_SELECTORS.VIDEO_PLAY_BTN_SMALL}"
      }, {
        "@type": "WebPageElement",
        "isAccessibleForFree": "False",
        "cssSelector": ".${PAYWALLED_CONTENT_SELECTORS.JOIN_US}"
      }
    ]
  }`;
};

const getRelatedVideosToDisplay = (
  relatedVideos: Video[] | null | undefined,
  nextVideoInPathwayId?: string | null,
) => {
  return relatedVideos
    ? relatedVideos.filter((item) => item?.id !== nextVideoInPathwayId).slice(0, 2)
    : [];
};

interface SingleVideoPageProps {
  isXApiPage: boolean;
  userInfo?: LearnerInfo;
  waitForUser?: boolean;
  bumperUrl?: string | null;
  tenantTitle?: string | null;
  setIsHeaderHidden: Dispatch<SetStateAction<boolean>>;
  tenantName?: string | null;
}

const SingleVideoPage: React.FC<SingleVideoPageProps> = ({
  userInfo,
  isXApiPage = false,
  waitForUser,
  bumperUrl,
  tenantTitle,
  setIsHeaderHidden,
  tenantName,
}) => {
  useMathJaxScriptLoader();
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location?.pathname;
  const width = useWidth();
  const isMobile = !isWidthUp('md', width);

  const search = location?.search;
  const queryParams = getQueryStringParams(search);
  const params = useParams();
  const videoSlug = params?.slug;
  const lastViewedPathwaySlug = Cookies.get(LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME);
  const [prevVideo, setPrevVideo] = useState<Video | null | undefined>(null);
  const [nextVideo, setNextVideo] = useState<Video | null | undefined>(null);
  const { isLoading, isAuthenticated } = useAuth();

  const userId = userInfo?.id ? userInfo.id : '';

  const iframeRef = useRef<HTMLFrameElement>(null);
  const { play, seek, goToChapter, onVideoClose } = useVerse(iframeRef.current?.contentWindow);

  const hasSubscription = (userInfo && userInfo.has_subscription) || isXApiPage;
  const isLoggedIn = !!userInfo;
  const noAuth = !isAuthenticated || !hasSubscription;
  const noAccess = !isXApiPage && noAuth;

  const classes = useStyles(hasSubscription, false)();
  const joinNowClasses = useJoinNowStyles();

  const [openJoinModal, setOpenJoinModal] = useState(false);
  const [videoPlayerLoaded, setVideoPlayerLoaded] = useState(false);

  const handleOpenJoinModal = () => {
    setOpenJoinModal(true);
  };

  const handleCloseJoinModal = () => {
    setOpenJoinModal(false);
  };

  const isIdInSlug = Number.isInteger(Number(videoSlug));

  const { data: videoData, loading: loadingVideo, error } = useQuery<
    GetVideoBySlugOrIdData,
    GetVideoBySlugOrIdArgs
  >(GET_VIDEO_BY_SLUG_OR_ID, {
    variables: {
      slug: isIdInSlug ? undefined : [videoSlug],
      id: isIdInSlug ? [videoSlug] : undefined,
    },
  });

  const video = videoData?.listVideos?.length ? videoData?.listVideos[0] : null;
  const videoId = video?.id ? video.id : '';
  const getVideo = !videoId || loadingVideo;

  const pathwayBySlug = useMemo(() => {
    return video?.pathways
      ? video?.pathways.find((item) => item?.slug === lastViewedPathwaySlug)
      : null;
  }, [video, lastViewedPathwaySlug]);

  const { data: relatedVideosData, loading: loadingRelatedVideos } = useQuery<
    Pick<Query, 'listVideosYouMightAlsoLike'>,
    QueryListVideosYouMightAlsoLikeArgs
  >(GET_RELATED_VIDEOS, {
    variables: {
      filter: {
        video_id: [videoId],
        user_id: userInfo?.id ? [userInfo.id] : null,
      },
    },
    skip: isXApiPage || getVideo,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const { data: nextInPathwayData, loading: loadingNextVideoInPathway } = useQuery<
    GetVideoNextInPathwayData,
    GetVideoNextInPathwayArgs
  >(GET_NEXT_IN_PATHWAY_VIDEO, {
    variables: {
      filter: {
        video_id: videoId,
        user_id: userInfo?.id,
      },
    },
    skip: isXApiPage || !hasSubscription || getVideo || !!pathwayBySlug,
    fetchPolicy: 'cache-first',
  });

  const nextVideoInPathway = nextInPathwayData?.getVideoNextInPathway;
  const nextVideoPathways = useMemo(() => nextVideoInPathway?.pathways || [], [nextVideoInPathway]);

  const pathway = useMemo(() => {
    const nextPathways = filterPathwaysArrByVideoId(nextVideoPathways, videoId);
    const pathways = lastViewedPathwaySlug
      ? video?.pathways
      : !!nextPathways
      ? nextPathways
      : video?.pathways;

    if (lastViewedPathwaySlug && pathwayBySlug) {
      return pathwayBySlug;
    } else if (pathways?.length === 1) {
      // add current pathway slug in cookies to solve problem when other videos of current pathway
      // exists in few pathways
      if (pathways?.[0] && lastViewedPathwaySlug !== pathways[0].slug) {
        Cookies.set(LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME, pathways[0].slug);
      }

      return pathways?.[0];
    }
    return null;
  }, [nextVideoPathways, videoId, lastViewedPathwaySlug, video, pathwayBySlug]);

  const showPathwayNavigation = useMemo(
    () => !isXApiPage && hasSubscription && video?.verse_video_url && !!pathway,
    [hasSubscription, isXApiPage, video, pathway],
  );

  const videoIndex = useMemo(() => {
    return !isXApiPage && pathway && pathway?.videos?.length
      ? pathway.videos.findIndex((item) => item?.id === video?.id) + 1
      : undefined;
  }, [pathway, video, isXApiPage]);

  const relatedVideos = relatedVideosData?.listVideosYouMightAlsoLike;
  const relatedVideosToDisplay = getRelatedVideosToDisplay(relatedVideos, nextVideo?.id);
  const continueWatchingIds = relatedVideosToDisplay
    ? relatedVideosToDisplay.map((item) => item?.id).filter((item) => !!item)
    : [];

  if (nextVideoInPathway?.id) {
    continueWatchingIds.push(nextVideoInPathway.id);
  }

  if (nextVideo?.id) {
    continueWatchingIds.push(nextVideo?.id);
  }

  if (prevVideo?.id) {
    continueWatchingIds.push(prevVideo?.id);
  }

  const { data: videosInWatchlist } = useQuery<
    Pick<Query, 'VideosExistInUserWatchList'>,
    QueryVideosExistInUserWatchListArgs
  >(GET_VIDEOS_EXIST_IN_USER_WATCH_LIST, {
    variables: {
      video_ids: continueWatchingIds,
    },
    skip: !continueWatchingIds || !continueWatchingIds?.length || !userInfo,
  });

  const watchlistById =
    videosInWatchlist?.VideosExistInUserWatchList?.reduce(
      collectValueByKey('video_id', 'exists'),
      {},
    ) || {};

  const { data: linksData } = useQuery<
    VideoContinueWatchingLinksData,
    VideoContinueWatchingLinksArgs
  >(GET_CONTINUE_WATCHING_VIDEO_LINKS, {
    variables: {
      filter: {
        user_id: userInfo ? userInfo.id : '',
        video_ids: continueWatchingIds as string[],
      },
    },
    skip:
      isXApiPage ||
      !hasSubscription ||
      loadingRelatedVideos ||
      loadingNextVideoInPathway ||
      continueWatchingIds.length === 0 ||
      getVideo,
    fetchPolicy: 'network-only',
  });

  const lastPositionData = getLinksObj(linksData?.listContinuingWatchingLinks);

  const { data: existsInWatchlistData } = useQuery<
    Pick<Query, 'VideoExistsInUserWatchList'>,
    VideoExistsInUserWatchListArgs
  >(CHECK_IF_VIDEO_EXISTS_IN_WATCHLIST, {
    variables: {
      filter: {
        user_id: userInfo?.id ? userInfo.id : '',
        video_id: videoId,
      },
    },
    skip: isXApiPage || !hasSubscription || getVideo,
    fetchPolicy: 'network-only',
  });

  const existsInWatchlist = !!existsInWatchlistData?.VideoExistsInUserWatchList?.exists;

  const { data: pageData } = useQuery<Pick<Query, 'getPageByUrl'>, GetPageByUrlArgs>(
    GET_PAGE_BY_URL,
    {
      variables: {
        url: VIDEO_PAGE_CUSTOM_BLOCKS_URL,
      },
      skip: hasSubscription || isXApiPage || getVideo || isLoading,
    },
  );

  const { data: reviewsList } = useQuery<Pick<Query, 'getListReviewsForEntity'>, listReviewsArgs>(
    GET_RATING_FOR_ENTITY,
    {
      variables: {
        entity_type: ReviewableType.Video,
        entity_id: video?.id || '',
        pagination_option: {
          page: 1,
          count_items_on_page: 10,
        },
      },
      skip: !video?.id || !isAuthenticated || isXApiPage,
    },
  );

  const page = pageData?.getPageByUrl;
  //filter seo-block to prevent conflicts with video page SEO
  const blocks = page?.blocks ? page.blocks.filter((item) => item?.type !== 'seo-block') : null;

  //calculate previous and next video in pathway
  useEffect(() => {
    if (videoIndex) {
      if (videoIndex !== 1) {
        setPrevVideo(pathway?.videos?.[videoIndex - 2]);
      } else {
        setPrevVideo(null);
      }
      if (videoIndex !== pathway?.videos?.length) {
        setNextVideo(pathway?.videos?.[videoIndex]);
      } else {
        setNextVideo(null);
      }
    }
  }, [videoIndex, pathway]);

  //set last viewed video position
  useEffect(() => {
    //works only after 'Story Viewed' event
    if (videoPlayerLoaded) {
      let chapter = Number(queryParams?.chapter);
      let time = Number(queryParams?.time);

      if (!isNaN(chapter) && !isNaN(time) && (chapter !== 0 || time !== 0)) {
        goToChapter(chapter);
        seek(time);
      }
    }
  }, [queryParams, videoPlayerLoaded, goToChapter, seek]);

  useEffect(() => {
    const handlePlayerLoaded = (event) => {
      if (event?.data?.event === 'Story Viewed') {
        setVideoPlayerLoaded(true);
      }
    };

    if (window) {
      window.addEventListener('message', handlePlayerLoaded);
    }

    return () => {
      if (window) {
        window.removeEventListener('message', handlePlayerLoaded);
      }
    };
  }, []);

  useEffect(() => {
    return () => onVideoClose();
  }, [pathname, onVideoClose]);

  useRmTag(RM_TAGS.VideoPage);

  const getCardLink = (id?: string | null, videoUrl?: string | null) => {
    if (id) {
      return generateContinueWatchingLink(
        `/videos/${videoUrl}/`,
        lastPositionData[id]?.chapter,
        lastPositionData[id]?.time,
      );
    } else {
      return `/videos/${videoUrl}`;
    }
  };

  let key_learning_objectives = video?.key_learning_objectives?.map((item) => item?.title);

  const scriptData = {
    customScriptType: 'application/ld+json',
    js_code: getVideoPageJsonData(video?.title, video?.description, key_learning_objectives),
  };

  const headerImage = video?.preview_url || generateRandomImageUrl(tenantName);
  const duration = video?.duration || 0;
  const rating = reviewsList?.getListReviewsForEntity?.general_star_rating;
  const showRating = isAuthenticated && !isMobile && !isXApiPage;

  const loading = loadingVideo || loadingRelatedVideos || loadingNextVideoInPathway || waitForUser;

  if (loading) {
    return (
      <>
        {video ? <VideoPageSeoBlock isXApiPage={isXApiPage} video={video} /> : null}
        <Loader />
      </>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorWrapper}>
        <Typography variant="h1">
          {t('error.error')}: {error?.message}
        </Typography>
      </Box>
    );
  }

  return video ? (
    <>
      <VideoPageSeoBlock isXApiPage={isXApiPage} video={video} />
      <Helmet>
        <title>
          {video.title} - {tenantTitle}
        </title>
      </Helmet>
      <JSBlock data={scriptData} />
      <Box className={classes.wrapper}>
        <ImageBackground
          hasSubscription={hasSubscription}
          preview={headerImage}
          title={video.title}
        />
        <Box className={classes.contentWrapper}>
          {hasSubscription ? (
            <LoggedInHeader
              video={video}
              handlePlay={play}
              videoLoaded={videoPlayerLoaded}
              isXApiPage={isXApiPage}
            >
              {!!pathway && (
                <PathwayNavigation
                  wrapperClass={classes.mobileNav}
                  pathwayName={pathway?.name}
                  pathwaySlug={pathway?.slug}
                />
              )}
            </LoggedInHeader>
          ) : (
            <LoggedOutHeader video={video} handleOpenJoinModal={handleOpenJoinModal} />
          )}
        </Box>
        {!hasSubscription && (
          <Modal className={classes.modal} open={openJoinModal}>
            <JoinNowModal handleClose={handleCloseJoinModal} isLoggedIn={isLoggedIn} />
          </Modal>
        )}
        <Container className={classes.videoWrap}>
          {showPathwayNavigation && (
            <PathwayNavigation
              wrapperClass={classes.desktopNav}
              pathwayName={pathway?.name}
              pathwaySlug={pathway?.slug}
              data-testid="pathwayNavigation"
            />
          )}
          <Box className={classes.videoBlock}>
            <div className={classes.blockWrap} id="playerWrap">
              {hasSubscription && video.verse_video_url && (
                <>
                  <div
                    className={cx(
                      classes.playerContainer,
                      !!pathway && !isXApiPage && classes.topBordered,
                    )}
                  >
                    <VideoPlayer
                      id="player"
                      className={classes.video}
                      duration={video?.duration}
                      title={video?.title}
                      expertName={video?.user?.name}
                      ref={iframeRef}
                      verseId={video?.verse_video_id}
                      bumperVideoUrl={video?.show_bumper ? bumperUrl : null}
                      onToggleFullscreen={setIsHeaderHidden}
                    />
                  </div>
                  {!isXApiPage && !!videoIndex && (
                    <PathwayPlayerMenu
                      videosCount={pathway?.videos?.length}
                      videoPosition={videoIndex}
                      prevVideoLink={getCardLink(prevVideo?.id, prevVideo?.video_url)}
                      nextVideoLink={getCardLink(nextVideo?.id, nextVideo?.video_url)}
                    />
                  )}
                  {!isXApiPage && pathway && (
                    <div className={classes.pathwayNameRow}>
                      <PathwaySmallIcon className={classes.pathwayIcon} />
                      <Trans i18nKey="page.video.videoModuleIsPart">
                        This video module is part of the
                        <Link
                          to={`/discover/pathways/${pathway?.slug}`}
                          className={classes.pathwayName}
                        >
                          {{ name: pathway?.name }}
                        </Link>
                        Pathway
                      </Trans>
                    </div>
                  )}
                  <VideoTitle
                    title={video?.title}
                    isXApiPage={isXApiPage}
                    existsInWatchlist={existsInWatchlist}
                    userId={userId}
                    videoId={videoId}
                    duration={duration}
                  />
                  {showRating && <Rating value={rating} />}
                </>
              )}
              <VideoTabsContainer
                showTabs={!noAccess}
                video={video}
                isXApiPage={isXApiPage}
                userId={userInfo?.id}
                rating={rating}
              />
            </div>
            {!isXApiPage && (relatedVideosToDisplay.length > 0 || nextVideo) && (
              <Box className={classes.videoCardsContainer}>
                {hasSubscription && nextVideo && (
                  <Box className={classes.videosBlock}>
                    <Typography variant="h3" className={classes.videosBlockTitle}>
                      {t('page.video.nextInPathway')}
                    </Typography>
                    <SideVideoCard
                      key={nextVideo.id}
                      id={nextVideo.id}
                      title={nextVideo.title}
                      author={nextVideo.user?.name}
                      duration={nextVideo.duration}
                      videoUrl={nextVideo.video_url}
                      categories={nextVideo.categories}
                      previewUrl={nextVideo.preview_url}
                      link={getCardLink(nextVideo.id, nextVideo?.video_url)}
                      difficulty={nextVideo.difficulty}
                      data-testid="nextVideoInPathwayCard"
                      isInWatchlist={watchlistById[nextVideo.id]}
                    />
                  </Box>
                )}
                {relatedVideosToDisplay.length > 0 && (
                  <Box className={classes.videosBlock}>
                    <Typography variant="h3" className={classes.videosBlockTitle}>
                      {t(hasSubscription ? 'page.video.alsoLike' : 'page.video.relatedVideos')}
                    </Typography>
                    {relatedVideosToDisplay.map((item) => (
                      <SideVideoCard
                        key={item.id}
                        id={item.id}
                        title={item?.title}
                        author={item?.user?.name}
                        duration={item?.duration}
                        categories={item?.categories}
                        previewUrl={item?.preview_url}
                        link={getCardLink(item.id, item?.video_url)}
                        difficulty={item?.difficulty}
                        data-testid="relatedVideosCard"
                        isInWatchlist={watchlistById[item.id]}
                      />
                    ))}
                    {noAccess && (
                      <JoinNowBlock
                        isLoggedIn={false}
                        classes={joinNowClasses}
                        description={t('page.video.logoutFreeTrial')}
                        btnCaption={t('button.startTrial')}
                        data-testid="stickyJoinNowBlock"
                      />
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Container>
        {blocks && blocks.length > 0 && <CustomPage blocks={blocks} />}
      </Box>
    </>
  ) : (
    <RouteWithCode code={404}>
      <NotFoundPage />
    </RouteWithCode>
  );
};

export default SingleVideoPage;
